import { apiClient } from '../client';

/**
 * ABP Setting value structure
 */
export interface SettingValue {
  name: string;
  value: string;
}

/**
 * Language list item from ABP
 * API returns { name: "English", value: "en" }
 */
export interface LanguageListItem {
  name: string;
  value: string;
}

/**
 * Language sync request model
 */
export interface LanguageSyncModel {
  languageCode: string;
  overrideLanguageTexts: boolean;
}

/**
 * Role options for permission reset
 */
export const ROLE_OPTIONS = [
  { name: 'All', value: 'all' },
  { name: 'Admin', value: 'admin' },
  { name: 'Internal', value: 'Internal' },
  { name: 'External User', value: 'External User' },
  { name: 'Client', value: 'client' },
] as const;

/**
 * Theme setting name constant
 */
export const THEME_SETTING_NAME = 'motr.Settings.ThemeOverride';

/**
 * User avatar color setting name constant
 * Stored as user-level setting (motr.User.* prefix)
 */
export const AVATAR_COLOR_SETTING_NAME = 'motr.User.AvatarColor';

/**
 * Settings Service
 * Interfaces with ABP Settings API for tenant-level settings
 */
export const settingsService = {
  /**
   * Get tenant settings by names (comma-separated)
   * @param names Comma-separated setting names
   */
  getSettings: async (names: string): Promise<SettingValue[]> => {
    const response = await apiClient.get<SettingValue[]>('/api/app/settings/settings', {
      params: { names },
    });
    return response.data;
  },

  /**
   * Get user-specific settings by names (comma-separated)
   * @param names Comma-separated setting names
   */
  getUserSettings: async (names: string): Promise<SettingValue[]> => {
    const response = await apiClient.get<SettingValue[]>('/api/app/settings/user-settings', {
      params: { names },
    });
    return response.data;
  },

  /**
   * Save settings (tenant-level for motr.Settings.*, user-level for motr.User.*)
   * @param settings Array of setting values to save
   */
  setSettings: async (settings: SettingValue[]): Promise<SettingValue[]> => {
    const response = await apiClient.post<SettingValue[]>('/api/app/settings/set-settings', settings);
    return response.data;
  },

  /**
   * Get theme override setting for current tenant
   */
  getThemeOverride: async (): Promise<string> => {
    const settings = await settingsService.getSettings(THEME_SETTING_NAME);
    const themeSetting = settings.find((s) => s.name === THEME_SETTING_NAME);
    return themeSetting?.value || '{}';
  },

  /**
   * Save theme override setting for current tenant
   * @param themeJson JSON string of theme override
   */
  setThemeOverride: async (themeJson: string): Promise<void> => {
    await settingsService.setSettings([
      {
        name: THEME_SETTING_NAME,
        value: themeJson,
      },
    ]);
  },

  /**
   * Get available languages for the tenant
   */
  getLanguages: async (): Promise<LanguageListItem[]> => {
    const response = await apiClient.get<LanguageListItem[]>('/api/app/settings/languages');
    return response.data;
  },

  /**
   * Sync language texts using AI translation
   * @param model Language sync model with language code and override flag
   */
  syncLanguageText: async (model: LanguageSyncModel): Promise<void> => {
    await apiClient.post('/api/app/settings/sync-language-text', model);
  },

  /**
   * Reset role permissions to defaults
   * @param role Role name to reset (or 'all' for all roles)
   */
  resetRolePermissions: async (role: string): Promise<void> => {
    await apiClient.post(`/api/app/settings/reset-role-permissions/${encodeURIComponent(role)}`);
  },

  /**
   * Clear application cache
   */
  clearCache: async (): Promise<void> => {
    await apiClient.get('/api/app/jobs-ext/ClearCache', {
      params: { key: 'all' },
    });
  },

  /**
   * Get avatar color for the current user
   * @returns Hex color string or null if not set
   */
  getAvatarColor: async (): Promise<string | null> => {
    const settings = await settingsService.getUserSettings(AVATAR_COLOR_SETTING_NAME);
    const colorSetting = settings.find((s) => s.name === AVATAR_COLOR_SETTING_NAME);
    return colorSetting?.value || null;
  },

  /**
   * Save avatar color for current user
   * @param color Hex color string (e.g., '#1e40af')
   */
  setAvatarColor: async (color: string): Promise<void> => {
    await settingsService.setSettings([
      {
        name: AVATAR_COLOR_SETTING_NAME,
        value: color,
      },
    ]);
  },
};
